import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { each, flattenDeep, trim } from 'lodash-es';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import { start as singleSpaStart } from 'single-spa';

import {
  CookieBannerProvider,
  ErrorProvider,
  ModuleLoaderProvider,
  ModulesProvider,
  OfflineCacheProvider,
  PlatformProvider,
  PreloginWrapper,
  UserProvider,
} from 'hooks';
import { CssBaseline, ThemeProvider, theme } from 'netline-ui';
import services, { initServices } from 'services';
import { getBaseTitle, getMainConfig, getMetaValue, logger, setBaseTitle } from 'utils';
import 'utils/i18n';
import { ErrorBoundary, NestedProviderDefs, NestedProviders } from 'view/components';

import App from './App';

import './index.css';

const cache = createCache({ key: 'appframe' });

const addViteHmrRefreshPreamble = () => {
  const hmrRefreshList = getMetaValue('hmr-refresh');
  if (!hmrRefreshList) {
    const scriptEl = document.createElement('script');
    scriptEl.type = 'module';
    const scriptContent: string[] = [];
    scriptContent.push(
      [
        'window.$RefreshReg$ = () => {};',
        'window.$RefreshSig$ = () => (type) => type;',
        'window.__vite_plugin_react_preamble_installed__ = true;',
      ].join('\n'),
    );
    scriptEl.textContent = flattenDeep(scriptContent).join('');
    document.head.prepend(scriptEl);

    return undefined;
  }

  return new Promise((resolve) => {
    const scriptEl = document.createElement('script');
    scriptEl.type = 'module';
    const scriptContent: string[] = [];
    scriptContent.push(
      hmrRefreshList
        .split(';')
        .map((hmrRefresh, idx) =>
          [
            `import ReactRefresh${idx} from '${hmrRefresh.trim()}';`,
            `ReactRefresh${idx}.injectIntoGlobalHook(window);`,
          ].join('\n'),
        )
        .join('\n'),
    );
    scriptContent.push(
      [
        'window.$RefreshReg$ = () => {};',
        'window.$RefreshSig$ = () => (type) => type;',
        'window.__vite_plugin_react_preamble_installed__ = true;',
      ].join('\n'),
    );
    scriptEl.textContent = flattenDeep(scriptContent).join('');
    document.head.prepend(scriptEl);
    setTimeout(resolve);
  });
};

const createImportMaps = () =>
  new Promise((resolve) => {
    const importMapSources = getMetaValue('import-maps')?.split(';') || [];
    importMapSources.forEach((src) => {
      const scriptEl = document.createElement('script');
      scriptEl.type = 'systemjs-importmap';
      scriptEl.src = encodeURI(trim(src));
      document.body.prepend(scriptEl);
    });

    setTimeout(resolve);
  });

const providerDefs: NestedProviderDefs = [
  ErrorProvider,
  ModuleLoaderProvider,
  [HashRouter, { future: { v7_startTransition: true, v7_relativeSplatPath: true } }],
  OfflineCacheProvider,
  PreloginWrapper,
  UserProvider,
  ErrorBoundary,
  PlatformProvider,
  ModulesProvider,
  CookieBannerProvider,
];

/**
 * Small script to request a lock from the browser.
 * This is used to prevent the browser from navigating away from the page while the user is in the middle of a transaction.
 * This is only used in the NetLine AppFrame, and is not necessary for other applications.
 */
const registerWebLock = async () => {
  const serviceWorkerAvailable = 'serviceWorker' in navigator;
  if (!serviceWorkerAvailable) {
    logger.info('WebLock is disabled due to browser incompatibility!');
    return;
  }
  if (getMainConfig('allowWebLock') === false) {
    logger.info('WebLock is not enabled in main configuration!');
    return;
  }

  try {
    var webLockResolver;
    if (navigator && navigator.locks && navigator.locks.request) {
      const promise = new Promise((resolve) => {
        webLockResolver = resolve;
      });
      const resourceId = `NetLine_AppFrame_WebLock_${Date.now()}`;
      navigator.locks.request(resourceId, { mode: 'shared' }, () => {
        logger.info('WebLock is activated.');
        return promise;
      });
    }
  } catch (error) {
    logger.error('Unable to register WebLock!', error);
  }
};

const injectDebugGlobals = () => {
  const setMode = (el: Element | null) => {
    const mode = el?.getAttribute?.('value');
    logger.info(`Operation mode set to ${mode}!`);
    if (mode === 'development') {
      window.services = services;
    } else {
      delete window.services;
    }
  };

  // start observing the target node for configured mutations
  const observer = new MutationObserver((mutationList) => {
    each(mutationList, (mutation) => {
      setMode(mutation.target as Element);
    });
  });

  const modeEl = document.querySelector('meta[name="mode"]');
  if (!modeEl) {
    return;
  }
  observer.observe(modeEl, { attributes: true });
  setMode(modeEl);
};

const startApp = async () => {
  await addViteHmrRefreshPreamble();
  await createImportMaps();

  singleSpaStart();

  await initServices();

  await registerWebLock();

  setBaseTitle(getMainConfig('title') || getBaseTitle());

  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element not found!');
  }
  const root = createRoot(rootEl);
  root.render(
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NestedProviders providers={providerDefs}>
          <App />
        </NestedProviders>
      </ThemeProvider>
    </CacheProvider>,
  );
};

if (window.cordova) {
  document.addEventListener('deviceready', startApp);
} else {
  startApp();
}

injectDebugGlobals();

window.process = window.process || { env: {} };
